import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider, CssBaseline, Box, CircularProgress } from "@mui/material";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./components/auth/Login";
import Dashboard from "./components/dashboard/Dashboard";
import RegistrationForm from "./components/auth/RegistrationForm";
import StatusPage from "./components/auth/StatusPage";
import PublicHomepage from "./components/public/PublicHomepage";
import ArticleDetail from "./components/public/ArticleDetail";
import ErrorBoundary from "./components/shared/ErrorBoundary";
import theme from "./theme";

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // DEBUG: Log user state in PrivateRoute
  console.log("[PrivateRoute] user:", user);
  console.log("[PrivateRoute] loading:", loading);
  console.log("[PrivateRoute] location:", location.pathname);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Level 1: Firewall check - Google login
  // Anyone not logged in must land on Login Page, irrespective of page
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Level 2: Firewall check - Status field
  // For logged in users, strictly follow the "status" field

  // 2a. If status is "approved", let user access what she wants
  if (user.status === "approved") {
    return <>{children}</>;
  }

  // 2b. For rejected/pending, no matter which page user wants to access,
  // always land on a page which shows message pending or rejected approvals,
  // with no other UI element. Full page. No banner.

  // Special case: If user is pending but hasn't submitted registration yet,
  // allow access to /register page only
  if (user.status === "pending" && !user.requestedRole) {
    if (location.pathname === "/register") {
      return <>{children}</>;
    }
    return <Navigate to="/register" replace />;
  }

  // For all other cases (rejected or pending with requestedRole),
  // show full page status message
  return <StatusPage />;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Allow both authenticated and unauthenticated users to access public routes
  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/"
        element={
          <PublicRoute>
            <PublicHomepage />
          </PublicRoute>
        }
      />
      <Route
        path="/news/:id"
        element={
          <PublicRoute>
            <ArticleDetail />
          </PublicRoute>
        }
      />

      {/* Auth Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PrivateRoute>
            <ErrorBoundary>
              <RegistrationForm />
            </ErrorBoundary>
          </PrivateRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/dashboard"
        element={<Navigate to="/admin" replace />}
      />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router
        future={{
          v7_relativeSplatPath: true,
        }}
      >
        <ErrorBoundary>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </ErrorBoundary>
      </Router>
    </ThemeProvider>
  );
};

export default App;

